import { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import { api } from "@/lib/api";
import { useReconnectingSocket } from "@/hooks/useReconnectingSocket";
import { isTelemetryStale } from "@/lib/commandGating";

const LiveDataContext = createContext(null);

export function LiveDataProvider({ children }) {
  const [drones, setDrones] = useState([]); // REST baseline
  const [live, setLive] = useState({}); // drone_id -> telemetry
  const [events, setEvents] = useState([]);
  const [fleetConnected, setFleetConnected] = useState(false);
  const [apiConnected, setApiConnected] = useState(null);
  const [lastEvent, setLastEvent] = useState(null);
  const seq = useRef(0);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/drones");
      setDrones(data.drones || []);
      if (data.last_event) setLastEvent(data.last_event);
      setApiConnected(true);
    } catch (e) {
      setApiConnected(false);
    }
  }, []);

  useEffect(() => { refresh(); const t = setInterval(refresh, 10000); return () => clearInterval(t); }, [refresh]);

  const pushEvent = useCallback((ev) => {
    const item = { ...ev, _id: ++seq.current, ts: new Date().toISOString() };
    setEvents((prev) => [item, ...prev].slice(0, 300));
    setLastEvent(item);
  }, []);

  const onMessage = useCallback((msg) => {
    const { type, data } = msg;
    if (type === "bridge_error") {
      pushEvent({ type: "ws_error", status: "error", message: data || msg.message || "Erreur WS flotte (Gateway Hetzner)", source: "Hetzner" });
      return;
    }
    // Format Gateway Hetzner : fleet_telemetry arrive PAR DRONE (data = un drone), pas un tableau.
    if (type === "fleet_telemetry" || type === "telemetry" || type === "telemetry_snapshot") {
      if (data?.drone_id) {
        setLive((p) => ({ ...p, [data.drone_id]: { ...p[data.drone_id], ...data } }));
      }
      return;
    }
    if (type === "event") {
      if (data) pushEvent(data);
      return;
    }
    // type "connected" et autres messages de service : ignorés.
  }, [pushEvent]);

  const fleetSocket = useReconnectingSocket({ path: "/api/ws/fleet", onMessage, onStatus: setFleetConnected });

  // Baseline REST (roster + métadonnées + 1er snapshot Hetzner) + override live WS Hetzner.
  // Altitude de vol = relative_alt UNIQUEMENT (jamais l'AMSL) : on normalise `alt` sur relative_alt.
  const mergedDrones = drones.map((d) => {
    const m = { ...d, ...(live[d.drone_id] || {}) };
    m.alt = m.relative_alt != null ? m.relative_alt : null;
    // CF1-02 — fail-closed : le descriptor flight_controller n'est AUTORITAIRE que si la télémétrie
    // correspondante est FRAÎCHE (last_seen live). Stale / missing -> non autoritaire (null), pour
    // ne JAMAIS consommer un descriptor périmé (badge identité, gating, préchecks). On ne fabrique
    // aucun descriptor ; les autres données live (position/batterie/mode) ne sont pas touchées.
    if (isTelemetryStale(m.last_seen)) m.flight_controller = null;
    return m;
  });

  return (
    <LiveDataContext.Provider value={{
      drones: mergedDrones, raw: drones, live, events, fleetConnected, apiConnected,
      lastEvent, refresh, pushEvent, reconnectFleet: fleetSocket.reconnect,
    }}>
      {children}
    </LiveDataContext.Provider>
  );
}

export const useLiveData = () => useContext(LiveDataContext);
